import React, { useMemo, useState } from 'react';
import './ServicesSection.css';

export const SERVICE_GROUPS = [
    {
        key: 'food',
        title: 'Food & Dining',
        icon: '🍽️',
        description: 'Meals, dining and food-related services',
        items: [
            'Breakfast',
            'Lunch',
            'Dinner',
            'All Meals',
            'Room Service',
            'Restaurant',
            'Cafe',
            'Bar',
            'BBQ',
            'Packed Meals'
        ]
    },
    {
        key: 'transport',
        title: 'Transport',
        icon: '🚗',
        description: 'Pickup, drop and transportation services',
        items: [
            'Airport Pickup',
            'Airport Drop',
            'Railway Station Pickup',
            'Railway Station Drop',
            'Local Transfers',
            'Car Rental',
            'Bike Rental',
            'Private Driver',
            'Shuttle Service'
        ]
    },
    {
        key: 'activities',
        title: 'Activities',
        icon: '🏕️',
        description: 'Activities and experiences available to guests',
        items: [
            'Trekking',
            'Camping',
            'Bonfire',
            'Nature Walk',
            'Cycling',
            'Kayaking',
            'Boating',
            'Fishing',
            'Swimming',
            'Indoor Games',
            'Outdoor Games',
            'Adventure Activities'
        ]
    },
    {
        key: 'wellness',
        title: 'Wellness',
        icon: '🧘',
        description: 'Relaxation and wellness services',
        items: ['Spa', 'Massage', 'Yoga', 'Gym', 'Sauna', 'Steam Room', 'Meditation', 'Wellness Center']
    },
    {
        key: 'housekeeping',
        title: 'Housekeeping',
        icon: '🧹',
        description: 'Cleaning and guest convenience services',
        items: [
            'Daily Housekeeping',
            'Laundry',
            'Dry Cleaning',
            'Ironing',
            'Luggage Storage',
            'Daily Room Cleaning',
            'Towel Change',
            'Bed Linen Change'
        ]
    },
    {
        key: 'business',
        title: 'Business & Work',
        icon: '💼',
        description: 'Facilities for work and business travellers',
        items: [
            'Business Center',
            'Meeting Room',
            'Conference Room',
            'Printer',
            'Scanner',
            'Workspace',
            'High-Speed Internet'
        ]
    }
];

const SERVICE_TYPES = [
    {
        value: 'included',
        label: 'Included',
        description: 'Included in the booking price'
    },
    {
        value: 'paid',
        label: 'Paid',
        description: 'Guest pays separately'
    },
    {
        value: 'on_request',
        label: 'On Request',
        description: 'Available when requested'
    }
];

const DEFAULT_SERVICES = {
    food: [],
    transport: [],
    activities: [],
    wellness: [],
    housekeeping: [],
    business: []
};

const normalizeServices = (services = {}) => ({
    ...DEFAULT_SERVICES,
    ...services,
    food: Array.isArray(services.food) ? services.food : [],
    transport: Array.isArray(services.transport) ? services.transport : [],
    activities: Array.isArray(services.activities) ? services.activities : [],
    wellness: Array.isArray(services.wellness) ? services.wellness : [],
    housekeeping: Array.isArray(services.housekeeping) ? services.housekeeping : [],
    business: Array.isArray(services.business) ? services.business : []
});

const normalizeServiceItem = (item) => {
    if (typeof item === 'string') {
        return {
            name: item,
            type: 'included',
            price: 0,
            currency: 'INR',
            unit: 'per_booking',
            description: '',
            active: true
        };
    }

    return {
        name: '',
        type: 'included',
        price: 0,
        currency: 'INR',
        unit: 'per_booking',
        description: '',
        active: true,
        ...item
    };
};

const SERVICE_UNITS = [
    {
        value: 'per_booking',
        label: 'Per Booking'
    },
    {
        value: 'per_person',
        label: 'Per Person'
    },
    {
        value: 'per_night',
        label: 'Per Night'
    },
    {
        // backend allows per_booking / per_person / per_night / per_unit only
        value: 'per_unit',
        label: 'Per Room / Tent'
    }
];

// "+ Add Custom Service" items (shown as their own group)
const CUSTOM_GROUP = {
    key: 'custom',
    title: 'Custom Services',
    icon: '+',
    description: 'Services you added yourself',
    items: []
};

const toNumber = (value) => {
    if (value === '' || value === null || value === undefined) {
        return 0;
    }

    const number = Number(value);

    return Number.isFinite(number) ? number : 0;
};

const ServicesSection = ({ formData, updateField, canEdit = true }) => {
    const services = useMemo(() => normalizeServices(formData?.services), [formData?.services]);

    const [editingService, setEditingService] = useState(null);

    const totalServices = Object.values(services).reduce((total, group) => total + group.length, 0);

    const includedCount = Object.values(services).reduce(
        (total, group) => total + group.filter((item) => normalizeServiceItem(item).type === 'included').length,
        0
    );

    const paidCount = Object.values(services).reduce(
        (total, group) => total + group.filter((item) => normalizeServiceItem(item).type === 'paid').length,
        0
    );

    const updateServices = (groupKey, values) => {
        if (!canEdit) return;

        updateField('services', {
            ...services,
            [groupKey]: values
        });
    };

    const findServiceIndex = (groupKey, serviceName) => {
        return (services[groupKey] || []).findIndex((item) => normalizeServiceItem(item).name === serviceName);
    };

    const toggleService = (groupKey, serviceName) => {
        if (!canEdit) return;

        const group = services[groupKey] || [];

        const index = findServiceIndex(groupKey, serviceName);

        if (index >= 0) {
            updateServices(
                groupKey,
                group.filter((_, itemIndex) => itemIndex !== index)
            );

            return;
        }

        updateServices(groupKey, [
            ...group,
            {
                name: serviceName,
                type: 'included',
                price: 0,
                currency: 'INR',
                unit: 'per_booking',
                description: '',
                active: true
            }
        ]);
    };

    const updateService = (groupKey, index, field, value) => {
        if (!canEdit) return;

        const group = [...(services[groupKey] || [])];

        group[index] = {
            ...normalizeServiceItem(group[index]),
            [field]: value
        };

        updateServices(groupKey, group);
    };

    const removeService = (groupKey, index) => {
        if (!canEdit) return;

        const group = services[groupKey] || [];

        updateServices(
            groupKey,
            group.filter((_, itemIndex) => itemIndex !== index)
        );
    };

    const addCustomService = () => {
        if (!canEdit) return;

        setEditingService({
            mode: 'new',
            groupKey: 'custom',
            index: -1,
            service: {
                name: '',
                type: 'included',
                price: 0,
                currency: 'INR',
                unit: 'per_booking',
                description: '',
                active: true
            }
        });
    };

    const saveCustomService = (groupKey, service) => {
        if (!canEdit) return;

        const name = service.name?.trim();

        if (!name) return;

        const currentGroup = services[groupKey] || [];

        if (editingService?.mode === 'edit' && editingService.groupKey === groupKey) {
            const updatedGroup = [...currentGroup];

            updatedGroup[editingService.index] = {
                ...service,
                name
            };

            updateServices(groupKey, updatedGroup);
        } else {
            updateServices(groupKey, [
                ...currentGroup,
                {
                    ...service,
                    name
                }
            ]);
        }

        setEditingService(null);
    };

    return (
        <div className='services-section'>
            {/* =====================================================
          HEADER
      ====================================================== */}

            <div className='services-header'>
                <div>
                    <span className='section-eyebrow'>SERVICES & EXPERIENCES</span>

                    <h2>Services</h2>

                    <p>Configure services, experiences and add-ons available to guests at this property.</p>
                </div>

                <div className='services-header-actions'>
                    <div className='service-stat'>
                        <strong>{totalServices}</strong>
                        <span>services</span>
                    </div>

                    <div className='service-stat'>
                        <strong>{includedCount}</strong>
                        <span>included</span>
                    </div>

                    <div className='service-stat'>
                        <strong>{paidCount}</strong>
                        <span>paid</span>
                    </div>
                </div>
            </div>

            {/* =====================================================
          SERVICE GROUPS
      ====================================================== */}

            <div className='service-groups'>
                {SERVICE_GROUPS.map((group) => {
                    const groupServices = services[group.key] || [];

                    const selectedNames = groupServices.map((item) => normalizeServiceItem(item).name);

                    return (
                        <ServiceGroup
                            key={group.key}
                            group={group}
                            services={groupServices}
                            selectedNames={selectedNames}
                            canEdit={canEdit}
                            onToggle={toggleService}
                            onUpdate={updateService}
                            onRemove={removeService}
                        />
                    );
                })}

                {/* custom services ("+ Add Custom Service") */}
                {(services.custom || []).length > 0 && (
                    <ServiceGroup
                        group={CUSTOM_GROUP}
                        services={services.custom || []}
                        selectedNames={[]}
                        canEdit={canEdit}
                        onToggle={toggleService}
                        onUpdate={updateService}
                        onRemove={removeService}
                    />
                )}
            </div>

            {/* =====================================================
          CUSTOM SERVICE
      ====================================================== */}

            <div className='custom-service-card'>
                <div className='custom-service-icon'>+</div>

                <div className='custom-service-content'>
                    <h3>Custom Service</h3>

                    <p>Add a service that is not available in the predefined categories.</p>
                </div>

                {canEdit && (
                    <button type='button' className='add-custom-service-btn' onClick={addCustomService}>
                        + Add Service
                    </button>
                )}
            </div>

            {/* =====================================================
          CUSTOM SERVICE MODAL
      ====================================================== */}

            {editingService && (
                <CustomServiceModal
                    service={editingService.service}
                    canEdit={canEdit}
                    onClose={() => setEditingService(null)}
                    onSave={(service) => saveCustomService(editingService.groupKey, service)}
                />
            )}

            {/* =====================================================
          INFO
      ====================================================== */}

            <div className='services-info'>
                <div className='services-info-icon'>💡</div>

                <div>
                    <strong>Service pricing</strong>

                    <p>
                        Services marked as <b>Included</b> are part of the booking price. Paid services can be charged
                        separately. Final service pricing and availability should be validated by the backend during
                        booking.
                    </p>
                </div>
            </div>
        </div>
    );
};

/* =========================================================
   SERVICE GROUP
========================================================= */

const ServiceGroup = ({ group, services, selectedNames, canEdit, onToggle, onUpdate, onRemove }) => {
    return (
        <div className='service-group-card'>
            <div className='service-group-header'>
                <div className='service-group-title'>
                    <div className='service-group-icon'>{group.icon}</div>

                    <div>
                        <h3>{group.title}</h3>

                        <p>{group.description}</p>
                    </div>
                </div>

                <span className='service-group-count'>{services.length} selected</span>
            </div>

            {/* Predefined services */}
            <div className='service-options-grid'>
                {group.items.map((item) => {
                    const selected = selectedNames.includes(item);

                    return (
                        <button
                            type='button'
                            key={item}
                            className={`service-option ${selected ? 'selected' : ''}`}
                            onClick={() => onToggle(group.key, item)}
                            disabled={!canEdit}
                        >
                            <span className={`service-check ${selected ? 'checked' : ''}`}>{selected ? '✓' : ''}</span>

                            <span>{item}</span>
                        </button>
                    );
                })}
            </div>

            {/* Selected service configuration */}
            {services.length > 0 && (
                <div className='selected-services'>
                    <div className='selected-services-header'>
                        <span>Service Configuration</span>
                    </div>

                    <div className='service-config-list'>
                        {services.map((rawService, index) => {
                            const service = normalizeServiceItem(rawService);

                            return (
                                <div className='service-config-row' key={`${service.name}-${index}`}>
                                    <div className='service-config-name'>
                                        <span className='service-config-check'>✓</span>

                                        <div>
                                            <strong>{service.name}</strong>

                                            {service.description && <small>{service.description}</small>}
                                        </div>
                                    </div>

                                    <div className='service-config-fields'>
                                        <select
                                            value={service.type}
                                            onChange={(event) => onUpdate(group.key, index, 'type', event.target.value)}
                                            disabled={!canEdit}
                                        >
                                            {SERVICE_TYPES.map((type) => (
                                                <option value={type.value} key={type.value}>
                                                    {type.label}
                                                </option>
                                            ))}
                                        </select>

                                        {service.type === 'paid' && (
                                            <>
                                                <div className='service-price-input'>
                                                    <span>₹</span>

                                                    <input
                                                        type='number'
                                                        min='0'
                                                        value={service.price || ''}
                                                        onChange={(event) =>
                                                            onUpdate(
                                                                group.key,
                                                                index,
                                                                'price',
                                                                toNumber(event.target.value)
                                                            )
                                                        }
                                                        placeholder='Price'
                                                        disabled={!canEdit}
                                                    />
                                                </div>

                                                <select
                                                    value={service.unit || 'per_booking'}
                                                    onChange={(event) =>
                                                        onUpdate(group.key, index, 'unit', event.target.value)
                                                    }
                                                    disabled={!canEdit}
                                                >
                                                    {SERVICE_UNITS.map((unit) => (
                                                        <option value={unit.value} key={unit.value}>
                                                            {unit.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </>
                                        )}

                                        {canEdit && (
                                            <button
                                                type='button'
                                                className='remove-service-btn'
                                                onClick={() => onRemove(group.key, index)}
                                                aria-label={`Remove ${service.name}`}
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

/* =========================================================
   CUSTOM SERVICE MODAL
========================================================= */

const CustomServiceModal = ({ service, canEdit, onClose, onSave }) => {
    const [form, setForm] = useState({
        name: service?.name || '',
        type: service?.type || 'included',
        price: service?.price || 0,
        currency: service?.currency || 'INR',
        unit: service?.unit || 'per_booking',
        description: service?.description || '',
        active: service?.active !== false
    });

    const update = (field, value) => {
        setForm((previous) => ({
            ...previous,
            [field]: value
        }));
    };

    const save = () => {
        if (!form.name.trim()) return;

        onSave({
            ...form,
            name: form.name.trim()
        });
    };

    return (
        <div className='service-modal-backdrop'>
            <div className='service-modal'>
                <div className='service-modal-header'>
                    <div>
                        <span>NEW SERVICE</span>

                        <h3>Add Custom Service</h3>
                    </div>

                    <button type='button' onClick={onClose}>
                        ×
                    </button>
                </div>

                <div className='service-modal-body'>
                    <div className='modal-field'>
                        <label>
                            Service Name
                            <span>*</span>
                        </label>

                        <input
                            type='text'
                            value={form.name}
                            onChange={(event) => update('name', event.target.value)}
                            placeholder='e.g. Private Bonfire Setup'
                            autoFocus
                            disabled={!canEdit}
                        />
                    </div>

                    <div className='modal-field'>
                        <label>Service Type</label>

                        <div className='service-type-options'>
                            {SERVICE_TYPES.map((type) => {
                                const selected = form.type === type.value;

                                return (
                                    <button
                                        type='button'
                                        key={type.value}
                                        className={selected ? 'selected' : ''}
                                        onClick={() => update('type', type.value)}
                                        disabled={!canEdit}
                                    >
                                        <strong>{type.label}</strong>

                                        <small>{type.description}</small>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {form.type === 'paid' && (
                        <div className='modal-price-grid'>
                            <div className='modal-field'>
                                <label>Price</label>

                                <div className='modal-money-input'>
                                    <span>₹</span>

                                    <input
                                        type='number'
                                        min='0'
                                        value={form.price || ''}
                                        onChange={(event) => update('price', toNumber(event.target.value))}
                                        placeholder='0'
                                        disabled={!canEdit}
                                    />
                                </div>
                            </div>

                            <div className='modal-field'>
                                <label>Price Unit</label>

                                <select
                                    value={form.unit}
                                    onChange={(event) => update('unit', event.target.value)}
                                    disabled={!canEdit}
                                >
                                    {SERVICE_UNITS.map((unit) => (
                                        <option value={unit.value} key={unit.value}>
                                            {unit.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    <div className='modal-field'>
                        <label>Description</label>

                        <textarea
                            value={form.description}
                            onChange={(event) => update('description', event.target.value)}
                            placeholder='Describe this service...'
                            rows={3}
                            disabled={!canEdit}
                        />
                    </div>
                </div>

                <div className='service-modal-footer'>
                    <button type='button' className='modal-cancel-btn' onClick={onClose}>
                        Cancel
                    </button>

                    <button
                        type='button'
                        className='modal-save-btn'
                        onClick={save}
                        disabled={!canEdit || !form.name.trim()}
                    >
                        Add Service
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ServicesSection;
